import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Content to display in the tooltip. Strings are most common; nodes are
   *  supported (AC-1313) so callers can embed e.g. a <pre> block to preserve
   *  newlines and monospace layout. */
  content: React.ReactNode;
  /** Preferred position (defaults to 'top') */
  position?: 'top' | 'bottom';
  children: React.ReactNode;
}

/**
 * Dark-mode-aware tooltip rendered via portal to escape overflow:hidden containers.
 * Replaces native browser title attributes which render in bright white in dark mode.
 */
const Tooltip: React.FC<TooltipProps> = ({ content, position = 'top', children }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  // Actual placement after auto-flip (may differ from the preferred `position`).
  const [placement, setPlacement] = useState<'top' | 'bottom'>(position);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const tooltipRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    timeoutRef.current = setTimeout(() => setVisible(true), 400);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
    setCoords(null);
  }, []);

  // Calculate position after tooltip becomes visible so we can measure it
  useEffect(() => {
    if (!visible || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tooltipWidth = tooltipEl?.offsetWidth ?? 0;
    const tooltipHeight = tooltipEl?.offsetHeight ?? 0;
    const GAP = 8;

    // Auto-flip to the opposite side when the preferred side would clip the
    // viewport edge (e.g. a top-positioned tooltip on an icon near the top of
    // the page). Guarded on a measured height so jsdom (which reports 0) keeps
    // the preferred placement and existing test behavior is unchanged.
    let place: 'top' | 'bottom' = position;
    if (tooltipHeight > 0) {
      if (place === 'top' && rect.top - tooltipHeight - GAP < 0) {
        place = 'bottom';
      } else if (place === 'bottom' && rect.bottom + tooltipHeight + GAP > window.innerHeight) {
        place = 'top';
      }
    }

    const top = place === 'top' ? rect.top - GAP : rect.bottom + GAP;

    // Center horizontally on trigger, but clamp to viewport
    let left = rect.left + rect.width / 2;
    if (tooltipWidth > 0) {
      const halfW = tooltipWidth / 2;
      if (left - halfW < 8) left = halfW + 8;
      if (left + halfW > window.innerWidth - 8) left = window.innerWidth - halfW - 8;
    }

    setPlacement(place);
    setCoords({ top, left });
  }, [visible, position]);

  if (!content) {
    return <>{children}</>;
  }

  const isTop = placement === 'top';

  return (
    <div className="relative inline-flex max-w-full" ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {visible && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={coords ? {
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: `translate(-50%, ${isTop ? '-100%' : '0'})`,
          } : {
            position: 'fixed',
            visibility: 'hidden',
          }}
          className="z-[9999] px-3 py-1.5 text-xs font-medium rounded-md shadow-lg max-w-xs bg-gray-900 text-gray-100 dark:bg-gray-700 dark:text-gray-100 pointer-events-none"
        >
          {content}
          <div
            className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${
              isTop
                ? 'top-full border-t-gray-900 dark:border-t-gray-700'
                : 'bottom-full border-b-gray-900 dark:border-b-gray-700'
            }`}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
